import { useTranslation } from 'react-i18next';

export interface UpgradePlanBadgeProps {
  planName: string;
  onClick: () => void;
}

/**
 * UpgradePlanBadge (D2) — componente presentacional puro: badge-morph "Mejorar plan".
 *
 * En reposo muestra el plan actual con el mismo aspecto que el `<Badge>` estático del
 * design-system; al hover/focus se rellena con el gradiente de marca y revela el segmento
 * "Mejorar ↑". El gating (feature flag + rol Admin + organización activa) NO vive aquí —
 * lo decide el consumidor (`OrgHeader`, Slice 2): si este componente se monta, se muestra.
 *
 * El CSS se traslada tal cual desde el bloque `<style>` que vivía en `MyOrganizationPage`
 * (E39/E45 — el JIT de Tailwind v4 no genera gradientes arbitrarios fuera de `@theme`).
 */
export function UpgradePlanBadge({ planName, onClick }: UpgradePlanBadgeProps) {
  const { t } = useTranslation();

  return (
    <>
      <button
        type="button"
        className="upgrade-plan-badge"
        onClick={onClick}
        aria-label={t('upgrade_plan.aria_label', { plan: planName })}
      >
        <span className="upgrade-plan-badge__label">
          {t('my_org.plan_label', { plan: planName })}
        </span>
        <span className="upgrade-plan-badge__divider" aria-hidden="true" />
        <span className="upgrade-plan-badge__extra">
          {t('upgrade_plan.cta_short')}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        </span>
      </button>

      <style>{`
        /* ── Badge-morph "Mejorar plan" (D2) ──────────────────────────────
           Reposo: mismo aspecto que el <Badge> del plan. Hover/focus: se rellena
           con el gradiente de marca y revela el segmento "Mejorar ↑". El gradiente
           usa var(--color-klonup-*) inline porque el JIT de Tailwind v4 no genera
           gradientes arbitrarios fuera de @theme (E39). */
        .upgrade-plan-badge {
          display: inline-flex; align-items: center; justify-content: center; gap: 0;
          font-family: 'Nunito', sans-serif; font-weight: 900; font-size: 11px;
          line-height: 1; text-transform: uppercase; letter-spacing: 0.1em; white-space: nowrap;
          background: #eff6ff; color: var(--color-klonup-sky); border: 1px solid #dbeafe;
          padding: 8px 14px; border-radius: 8px; cursor: pointer; overflow: hidden;
          transition: all 0.28s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .upgrade-plan-badge__label { line-height: 1; }
        /* En reposo el divisor está colapsado (width 0) para que el label quede
           centrado; al hover se expande junto con el segmento "Mejorar". */
        .upgrade-plan-badge__divider {
          width: 0; height: 12px; background: transparent;
          transition: width 0.28s ease, margin 0.28s ease, background 0.28s ease;
        }
        .upgrade-plan-badge__extra {
          display: inline-flex; align-items: center; gap: 6px;
          max-width: 0; opacity: 0; overflow: hidden; white-space: nowrap;
          transition: max-width 0.28s ease, opacity 0.2s ease, margin 0.28s ease;
        }
        .upgrade-plan-badge__extra svg { width: 13px; height: 13px; }
        .upgrade-plan-badge:hover, .upgrade-plan-badge:focus-visible {
          background: linear-gradient(135deg, var(--color-klonup-indigo), var(--color-klonup-sky));
          color: #fff; border-color: transparent;
          box-shadow: 0 6px 18px rgba(0, 99, 234, 0.35);
          transform: translateY(-1px); outline: none;
        }
        .upgrade-plan-badge:hover .upgrade-plan-badge__divider,
        .upgrade-plan-badge:focus-visible .upgrade-plan-badge__divider {
          width: 1px; margin: 0 7px; background: rgba(255, 255, 255, 0.4);
        }
        .upgrade-plan-badge:hover .upgrade-plan-badge__extra,
        .upgrade-plan-badge:focus-visible .upgrade-plan-badge__extra {
          max-width: 120px; opacity: 1;
        }
      `}</style>
    </>
  );
}
